import { SetServiceDependencies } from '../../infra/startup/types';
import bindAllMethods from '../../utils/bindAllMethods';
import matchPath from '../../utils/matchPath';
import * as T from '../../interface/v1/Fallback/types';
import EventNames from '../../config/eventNames';
import TenantLevelEnum from '../tenantHandler/TenantLevelEnum';
import IFallbackService from './IFallbackService';
import { InternalService } from '../../core/services';

/**
 * Concrete implementation of the Fallback Service
 * Manages fallback strategies and navigation for error handling
 */
export default class FallbackService
  extends InternalService
  implements IFallbackService
{
  protected fallbackList: T.FallbackConfigurationType['fallbackList'];
  protected state: T.FallbackStateType;
  protected eventService: any;
  protected navigationInterface: any;
  protected startedListeners = false;
  public defaultFallback: T.FallbackStrategyObjectType;
  public eventName: string;

  constructor(fallbackConfiguration: T.FallbackConstructorOptionsType) {
    super();

    // Set fallback-specific dependencies on the service
    this.setFallbackDependencies(fallbackConfiguration);

    bindAllMethods(this);
  }

  /**
   * Base setDependencies for InternalService
   */
  setDependencies(dependencies: SetServiceDependencies): void {
    this.eventService = dependencies.services.eventService;
    this.navigationInterface = dependencies.services.navigationService;
  }

  /**
   * Set fallback-specific dependencies
   */
  setFallbackDependencies(
    dependencies: T.FallbackConstructorOptionsType
  ): void {
    this.eventName = EventNames.globalFallback;
    this.fallbackList = dependencies?.configuration?.fallbackList;

    this.handleDefaultFallback(dependencies);
  }

  /**
   * Initialize and start the service
   */
  async init(): Promise<void> {
    await this.start();
    this._markAsReady();
  }

  /**
   * Start listening to navigation events
   */
  async start(): Promise<void> {
    this.startListeners();
  }

  /**
   * Handle default fallback configuration
   */
  private handleDefaultFallback(
    options: T.FallbackConstructorOptionsType
  ): void {
    // Try to find defaultFallback using its key
    if (options?.configuration?.defaultFallbackKey) {
      this.defaultFallback = this.getFallbackByKey(
        options?.configuration?.defaultFallbackKey
      );
      return;
    }
    if (!this.defaultFallback) {
      console.warn(
        'Default Fallback Key is invalid. Please, check if the key is correct.'
      );
      // If not found, try to use the defaultFallback object
      this.defaultFallback =
        options?.configuration?.defaultFallback ||
        ({} as T.FallbackStrategyObjectType);
      return;
    }
  }

  /**
   * Start listening to URL changes
   */
  private startListeners(): void {
    if (!this.startedListeners) {
      this.startedListeners = true;

      const urlListener = () => {
        const currentPath = this.navigationInterface.location.pathname;
        const currentFallback = this.getCurrentFallback();
        const fallback =
          this.getFallbackByPath(currentPath) ||
          currentFallback ||
          this.defaultFallback;

        const fallbackProperties = this.getFallbackPropertiesFromQueryParam();

        const isFallbackPath = (() => {
          if (fallback?.strategy === 'loadAsset' && fallback?.path) {
            return matchPath(currentPath, {
              pathToCompare: fallback?.path
            });
          }
          return false;
        })();

        if (!isFallbackPath) {
          this.clearFallbackState();
        } else {
          this.setCurrentFallback(fallback, { fallbackProperties });
        }
      };

      window.addEventListener('popstate', urlListener);
      urlListener();
    }
  }

  /**
   * Get fallback properties from URL query parameters
   */
  private getFallbackPropertiesFromQueryParam(): T.FallbackPropertiesType {
    const urlSearchParams = new URLSearchParams(window.location.search);

    const fallbackProperties: T.FallbackPropertiesType = {};

    urlSearchParams.forEach((value, key) => {
      if (value && key) {
        fallbackProperties[key] = value;
      }
    });

    return fallbackProperties;
  }

  /**
   * Get fallback configuration by key
   */
  getFallbackByKey(key: string): T.FallbackLoadAssetStrategyType | undefined {
    if (key) {
      return this.fallbackList?.find?.(
        (fallbackDescription) => fallbackDescription?.key === key
      );
    }
  }

  /**
   * Get default root fallback
   */
  getDefaultRootFallback(): T.FallbackRedirectStrategyType {
    return this.defaultFallback as T.FallbackRedirectStrategyType;
  }

  /**
   * Get fallback configuration by path
   */
  getFallbackByPath(path: string): T.FallbackLoadAssetStrategyType | undefined {
    const isFallbackPath = (fallbackPath: string) => {
      if (fallbackPath) {
        return matchPath(fallbackPath, {
          exact: true,
          pathToCompare: path
        });
      }
    };

    // Avoid issue loading defaultFallback if path is repeated in list
    const defaultFallback =
      this.defaultFallback?.strategy === 'loadAsset'
        ? this.defaultFallback
        : undefined;
    if (isFallbackPath(defaultFallback?.path)) {
      return defaultFallback;
    } else {
      return this.fallbackList?.find?.((fallbackDescription) =>
        isFallbackPath(fallbackDescription?.path)
      );
    }
  }

  /**
   * Get current fallback state
   */
  getCurrentFallback(): T.FallbackStateType {
    return this.state;
  }

  /**
   * Listen to fallback state changes
   */
  listen(callback: (state: T.FallbackStateType) => void): () => void {
    const callbackProxy = async () => {
      callback?.(this.getCurrentFallback());
    };

    this.eventService?.addEventListener?.(this.eventName, callbackProxy);

    return () =>
      this.eventService?.removeEventListener?.(this.eventName, callbackProxy);
  }

  /**
   * React hook to get current fallback state
   */
  useReactHook(React: any): T.FallbackLoadAssetStrategyType {
    const [state, setState] = React.useState(this.getCurrentFallback());

    React.useEffect(() => {
      const removeListener = this.listen((value) => setState(value));

      return () => removeListener();
    }, []);

    return state;
  }

  /**
   * Clear current fallback state
   */
  clearFallbackState(): void {
    this.setCurrentFallback(undefined);
  }

  /**
   * Build the final redirect URL by applying path and query parameter
   * forwarding options from the fallback configuration.
   */
  private buildRedirectUrl(
    fallback: T.FallbackRedirectStrategyType
  ): string | undefined {
    const { redirectTo, includePath, includeQueryparams, includeTenantId } =
      fallback;

    if (!redirectTo) return redirectTo;
    if (!includePath && !includeQueryparams && !includeTenantId) {
      return redirectTo;
    }

    const {
      pathname: currentPathname,
      search: currentSearch = '',
      hash: currentHash = ''
    } = this.navigationInterface.location;
    const currentSearchParams = new URLSearchParams(currentSearch);

    const isAbsolute =
      redirectTo.startsWith('http://') || redirectTo.startsWith('https://');
    // Use a placeholder origin so relative paths can be parsed uniformly;
    // only pathname/search/hash are used from the resulting URL.
    const targetUrl = isAbsolute
      ? new URL(redirectTo)
      : new URL(redirectTo, 'https://_placeholder_');

    if (includePath) {
      targetUrl.pathname =
        targetUrl.pathname.replace(/\/$/, '') + currentPathname;
    }

    if (includeQueryparams) {
      currentSearchParams.forEach((value, key) => {
        targetUrl.searchParams.set(key, value);
      });
    } else if (includeTenantId) {
      const tenantParams = this._extractTenantQueryParams(currentSearchParams);
      Object.entries(tenantParams).forEach(([key, value]) => {
        targetUrl.searchParams.set(key, value);
      });
    }

    if (isAbsolute) {
      return targetUrl.toString();
    }
    return targetUrl.pathname + targetUrl.search + currentHash;
  }

  /**
   * Extracts tenant query parameters (`t0`, `t1`, `t2`, …) from the given
   * URLSearchParams, based on the levels defined in TenantLevelEnum.
   * Returns a key-value map of the tenant params that are actually present.
   */
  private _extractTenantQueryParams(
    searchParams: URLSearchParams
  ): Record<string, string> {
    const result: Record<string, string> = {};
    Object.values(TenantLevelEnum)
      .filter((level): level is number => typeof level === 'number')
      .forEach((level) => {
        const paramKey = `t${level}`;
        const value = searchParams.get(paramKey);
        if (value) {
          result[paramKey] = value;
        }
      });
    return result;
  }

  /**
   * Set current fallback configuration
   */
  setCurrentFallback(
    fallback?: T.FallbackStrategyObjectType,
    options?: T.SetCurrentFallbackOptionsType
  ): void {
    let newFallback: T.FallbackStateType = undefined;
    let redirect: string;

    if (fallback?.strategy === 'redirect') {
      newFallback = undefined;
      redirect = this.buildRedirectUrl(fallback);
    } else if (fallback?.strategy === 'loadAsset') {
      newFallback = {
        ...fallback,
        fallbackProperties: options?.fallbackProperties
      };
      redirect = fallback.path;
    }

    if (JSON.stringify(this.state) !== JSON.stringify(newFallback)) {
      this.state = newFallback;
      this.eventService.publish(this.eventName, this.state);
    }

    if (
      redirect &&
      !matchPath(redirect, {
        exact: true,
        pathToCompare: this.navigationInterface.location.pathname
      })
    ) {
      const isExternalUrl =
        redirect.startsWith('http://') || redirect.startsWith('https://');

      if (isExternalUrl) {
        this.navigationInterface.redirect(redirect);
      } else {
        this.navigationInterface.push(redirect);
      }
    }
  }

  /**
   * Set current fallback by key identifier
   */
  setCurrentFallbackByKey(
    key: string,
    options?: T.SetCurrentFallbackOptionsType
  ): void {
    const fallback = this.getFallbackByKey(key) || this.defaultFallback;

    this.setCurrentFallback(fallback, options);
  }
}
